import { Schema } from "effect";

import { TrimmedNonEmptyString } from "./baseSchemas.ts";

export const DEVICE_PLATFORMS = ["ios", "android"] as const;
export const DevicePlatform = Schema.Literals(DEVICE_PLATFORMS);
export type DevicePlatform = typeof DevicePlatform.Type;

export const DEVICE_STREAM_ROUTE_PREFIX = "/api/device/stream";

export const DeviceKind = Schema.Literals(["phone", "tablet", "watch", "unknown"]);
export type DeviceKind = typeof DeviceKind.Type;

export const DeviceLifecycleState = Schema.Literals([
  "shutdown",
  "booting",
  "booted",
  "offline",
  "unauthorized",
]);
export type DeviceLifecycleState = typeof DeviceLifecycleState.Type;

export const DeviceTransport = Schema.Literals(["serve-sim", "scrcpy", "screenshot"]);
export type DeviceTransport = typeof DeviceTransport.Type;

export const DeviceCapabilitySet = Schema.Struct({
  liveVideo: Schema.Boolean,
  touch: Schema.Boolean,
  multiTouch: Schema.Boolean,
  keyboard: Schema.Boolean,
  home: Schema.Boolean,
  rotate: Schema.Boolean,
  mediaDrop: Schema.Boolean,
  screenshots: Schema.Boolean,
  logs: Schema.Boolean,
  accessibility: Schema.Boolean,
  permissions: Schema.Boolean,
  install: Schema.Boolean,
  launch: Schema.Boolean,
  physicalDevice: Schema.Boolean,
});
export type DeviceCapabilitySet = typeof DeviceCapabilitySet.Type;

export const DeviceDescriptor = Schema.Struct({
  id: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  platform: DevicePlatform,
  kind: DeviceKind,
  deviceTypeIdentifier: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(512))),
  state: DeviceLifecycleState,
  runtime: Schema.NullOr(Schema.String.check(Schema.isMaxLength(256))),
  physical: Schema.Boolean,
  capabilities: DeviceCapabilitySet,
});
export type DeviceDescriptor = typeof DeviceDescriptor.Type;

export const DeviceToolchainStatus = Schema.Struct({
  platform: DevicePlatform,
  available: Schema.Boolean,
  command: Schema.String,
  detail: Schema.String.check(Schema.isMaxLength(1_000)),
});
export type DeviceToolchainStatus = typeof DeviceToolchainStatus.Type;

export const DeviceStream = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
  requestedDeviceId: Schema.optional(TrimmedNonEmptyString),
  platform: DevicePlatform,
  transport: DeviceTransport,
  url: Schema.NullOr(Schema.String),
  streamUrl: Schema.NullOr(Schema.String),
  wsUrl: Schema.NullOr(Schema.String),
  width: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
  height: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
  attachedAt: Schema.String,
});
export type DeviceStream = typeof DeviceStream.Type;

export const DeviceActionActor = Schema.Literals(["user", "agent"]);
export type DeviceActionActor = typeof DeviceActionActor.Type;

export const DEVICE_ACTION_NAMES = [
  "discover",
  "attach",
  "detach",
  "shutdown",
  "install",
  "launch",
  "terminate",
  "tap",
  "swipe",
  "pinch",
  "type",
  "button",
  "rotate",
  "screenshot",
  "logs",
  "accessibility",
  "events",
  "openUrl",
  "addMedia",
  "permission",
] as const;
export const DeviceActionName = Schema.Literals(DEVICE_ACTION_NAMES);
export type DeviceActionName = typeof DeviceActionName.Type;

export const DeviceActionRecord = Schema.Struct({
  id: TrimmedNonEmptyString,
  deviceId: Schema.NullOr(Schema.String),
  platform: Schema.NullOr(DevicePlatform),
  action: DeviceActionName,
  actor: DeviceActionActor,
  startedAt: Schema.String,
  completedAt: Schema.String,
  status: Schema.Literals(["succeeded", "failed"]),
  summary: Schema.String.check(Schema.isMaxLength(500)),
  errorTag: Schema.optional(TrimmedNonEmptyString),
});
export type DeviceActionRecord = typeof DeviceActionRecord.Type;

export const DeviceWorkspaceSnapshot = Schema.Struct({
  hostPlatform: Schema.Literals(["darwin", "win32", "linux"]),
  toolchains: Schema.Array(DeviceToolchainStatus),
  devices: Schema.Array(DeviceDescriptor),
  streams: Schema.Array(DeviceStream),
  actions: Schema.Array(DeviceActionRecord),
  refreshedAt: Schema.String,
});
export type DeviceWorkspaceSnapshot = typeof DeviceWorkspaceSnapshot.Type;

export const DeviceListInput = Schema.Struct({
  includeActions: Schema.optional(Schema.Boolean),
});
export type DeviceListInput = typeof DeviceListInput.Type;

export const DeviceTargetInput = Schema.Struct({
  platform: DevicePlatform,
  deviceId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
});
export type DeviceTargetInput = typeof DeviceTargetInput.Type;

export const DeviceAttachInput = Schema.Struct({
  ...DeviceTargetInput.fields,
  boot: Schema.optional(Schema.Boolean),
});
export type DeviceAttachInput = typeof DeviceAttachInput.Type;

export const DeviceDetachInput = DeviceTargetInput;
export type DeviceDetachInput = typeof DeviceDetachInput.Type;

const NormalizedCoordinate = Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 }));

export const DeviceCommandInput = Schema.Union([
  Schema.TaggedStruct("shutdown", {
    ...DeviceTargetInput.fields,
  }),
  Schema.TaggedStruct("install", {
    ...DeviceTargetInput.fields,
    artifactPath: TrimmedNonEmptyString.check(Schema.isMaxLength(4_096)),
  }),
  Schema.TaggedStruct("launch", {
    ...DeviceTargetInput.fields,
    appId: TrimmedNonEmptyString.check(Schema.isMaxLength(512)),
  }),
  Schema.TaggedStruct("terminate", {
    ...DeviceTargetInput.fields,
    appId: TrimmedNonEmptyString.check(Schema.isMaxLength(512)),
  }),
  Schema.TaggedStruct("tap", {
    ...DeviceTargetInput.fields,
    x: NormalizedCoordinate,
    y: NormalizedCoordinate,
  }),
  Schema.TaggedStruct("swipe", {
    ...DeviceTargetInput.fields,
    startX: NormalizedCoordinate,
    startY: NormalizedCoordinate,
    endX: NormalizedCoordinate,
    endY: NormalizedCoordinate,
    durationMs: Schema.optional(
      Schema.Int.check(Schema.isBetween({ minimum: 50, maximum: 5_000 })),
    ),
  }),
  Schema.TaggedStruct("pinch", {
    ...DeviceTargetInput.fields,
    centerX: NormalizedCoordinate,
    centerY: NormalizedCoordinate,
    scale: Schema.Finite.check(Schema.isBetween({ minimum: 0.1, maximum: 5 })),
    durationMs: Schema.optional(
      Schema.Int.check(Schema.isBetween({ minimum: 50, maximum: 5_000 })),
    ),
  }),
  Schema.TaggedStruct("type", {
    ...DeviceTargetInput.fields,
    text: Schema.String.check(Schema.isMaxLength(10_000)),
  }),
  Schema.TaggedStruct("button", {
    ...DeviceTargetInput.fields,
    button: Schema.Literals([
      "home",
      "swipe_home",
      "app_switcher",
      "lock",
      "side_button",
      "siri",
      "volume_up",
      "volume_down",
      "enter",
      "back",
      "menu",
    ]),
  }),
  Schema.TaggedStruct("rotate", {
    ...DeviceTargetInput.fields,
    orientation: Schema.Literals([
      "portrait",
      "portrait_upside_down",
      "landscape_left",
      "landscape_right",
    ]),
  }),
  Schema.TaggedStruct("screenshot", {
    ...DeviceTargetInput.fields,
  }),
  Schema.TaggedStruct("logs", {
    ...DeviceTargetInput.fields,
    appId: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(512))),
    lines: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 5_000 }))),
  }),
  Schema.TaggedStruct("accessibility", {
    ...DeviceTargetInput.fields,
  }),
  Schema.TaggedStruct("events", {
    ...DeviceTargetInput.fields,
    limit: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 500 }))),
  }),
  Schema.TaggedStruct("openUrl", {
    ...DeviceTargetInput.fields,
    url: TrimmedNonEmptyString.check(Schema.isMaxLength(4_096)),
  }),
  Schema.TaggedStruct("addMedia", {
    ...DeviceTargetInput.fields,
    fileName: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
    mimeType: Schema.Literals([
      "image/jpeg",
      "image/png",
      "image/heic",
      "video/mp4",
      "video/quicktime",
    ]),
    data: Schema.String.check(Schema.isMaxLength(36_700_000)),
  }),
  Schema.TaggedStruct("permission", {
    ...DeviceTargetInput.fields,
    appId: TrimmedNonEmptyString.check(Schema.isMaxLength(512)),
    permission: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
    state: Schema.Literals(["grant", "revoke", "reset"]),
  }),
]);
export type DeviceCommandInput = typeof DeviceCommandInput.Type;

export const DeviceScreenshot = Schema.Struct({
  mimeType: Schema.Literal("image/png"),
  data: Schema.String,
  width: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
  height: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
});
export type DeviceScreenshot = typeof DeviceScreenshot.Type;

export const DeviceCommandResult = Schema.Struct({
  snapshot: DeviceWorkspaceSnapshot,
  output: Schema.optional(Schema.String.check(Schema.isMaxLength(2_000_000))),
  screenshot: Schema.optional(DeviceScreenshot),
});
export type DeviceCommandResult = typeof DeviceCommandResult.Type;

export class DeviceOperationError extends Schema.TaggedErrorClass<DeviceOperationError>()(
  "DeviceOperationError",
  {
    operation: DeviceActionName,
    platform: Schema.NullOr(DevicePlatform),
    deviceId: Schema.NullOr(Schema.String),
    reason: Schema.Literals([
      "unsupported-host",
      "toolchain-unavailable",
      "device-not-found",
      "device-not-ready",
      "invalid-input",
      "command-failed",
      "timeout",
      "stream-unavailable",
      "output-limit",
      "unexpected",
    ]),
    detail: Schema.String.check(Schema.isMaxLength(2_000)),
  },
) {
  override get message(): string {
    const target = this.deviceId ? ` for ${this.deviceId}` : "";
    return `Device ${this.operation}${target} failed: ${this.detail}`;
  }
}
